import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Plus, Download, Pen, Trash2, Loader2, ArrowLeft,
  CheckCircle, Clock, AlertTriangle, Eye, Edit, ChevronRight, MessageCircle, Lock,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Signature Pad Component (mobile/touch optimized) ─────────
export function SignaturePad({ onSave, onCancel, signerLabel }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasStrokes, setHasStrokes] = useState(false);

  const setupCanvas = useCallback((preserve = false) => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = Math.min(wrap.clientWidth - 2, 1260);
    const cssH = window.innerWidth < 640 ? 260 : 200;
    let prev = null;
    if (preserve) { try { prev = canvas.toDataURL('image/png'); } catch (e) {} }
    canvas.style.width = cssW + 'px';
    canvas.style.height = cssH + 'px';
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cssW, cssH);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = window.innerWidth < 640 ? 3 : 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (prev) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, cssW, cssH);
      img.src = prev;
    }
  }, []);

  useEffect(() => {
    setupCanvas(false);
    const onResize = () => setupCanvas(true);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [setupCanvas]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches && e.touches.length ? e.touches[0] : e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    if (!hasStrokes) setHasStrokes(true);
  };

  const endDraw = (e) => {
    if (e && e.cancelable) e.preventDefault();
    drawingRef.current = false;
  };

  const clearCanvas = () => {
    setHasStrokes(false);
    setupCanvas(false);
  };

  const saveSignature = () => {
    if (!hasStrokes) return;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-zinc-400">{signerLabel}</p>
      <div ref={wrapRef} className="relative w-full">
        <div className="border border-white/20 rounded-md overflow-hidden bg-white">
          <canvas
            ref={canvasRef}
            className="cursor-crosshair block w-full"
            style={{ touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={endDraw}
            onTouchCancel={endDraw}
            data-testid="signature-canvas"
          />
        </div>
        <div className="pointer-events-none absolute bottom-3 left-4 right-4 flex items-center gap-2 text-zinc-300">
          <span className="text-lg leading-none">✕</span>
          <span className="flex-1 border-b border-dashed border-zinc-300" />
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={clearCanvas} className="text-zinc-300 border-white/20 h-9 px-4" data-testid="signature-clear">
          Limpar
        </Button>
        <Button size="sm" onClick={saveSignature} disabled={!hasStrokes} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-5" data-testid="signature-save">
          <Pen className="w-3.5 h-3.5 mr-1" /> Assinar
        </Button>
        {onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel} className="text-zinc-400 h-9" data-testid="signature-cancel">
            Cancelar
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Contract Form Modal ──────────────────────────────────────
function ContractFormModal({ open, onClose, onSaved, contract }) {
  const { t } = useTranslation();
  const isEdit = !!contract;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    lender_name: 'Antônio Narciso Ramos Neto',
    lender_cpf: '',
    lender_address: '',
    lender_nationality: 'Brasileiro',
    lender_phone: '',
    borrower_name: "Presage Reit's Control Ltda",
    borrower_cnpj: '',
    borrower_address: '',
    borrower_representative: '',
    borrower_phone: '',
    amount: 5000,
    currency: 'BRL',
    interest_rate_monthly: 1.0,
    term_months: 12,
    installments: 12,
    late_fee_pct: 2.0,
    late_interest_monthly: 1.0,
    jurisdiction: 'São Paulo',
    contract_date: new Date().toISOString().slice(0, 10),
    purpose: 'Capital de giro',
  });

  useEffect(() => {
    if (contract) {
      setForm({
        lender_name: contract.lender_name || '',
        lender_cpf: contract.lender_cpf || '',
        lender_address: contract.lender_address || '',
        lender_nationality: contract.lender_nationality || 'Brasileiro',
        lender_phone: contract.lender_phone || '',
        borrower_name: contract.borrower_name || '',
        borrower_cnpj: contract.borrower_cnpj || '',
        borrower_address: contract.borrower_address || '',
        borrower_representative: contract.borrower_representative || '',
        borrower_phone: contract.borrower_phone || '',
        amount: contract.amount || 5000,
        currency: contract.currency || 'BRL',
        interest_rate_monthly: contract.interest_rate_monthly || 1.0,
        term_months: contract.term_months || 12,
        installments: contract.installments || 12,
        late_fee_pct: contract.late_fee_pct || 2.0,
        late_interest_monthly: contract.late_interest_monthly || 1.0,
        jurisdiction: contract.jurisdiction || 'São Paulo',
        contract_date: contract.contract_date || new Date().toISOString().slice(0, 10),
        purpose: contract.purpose || 'Capital de giro',
      });
    }
  }, [contract]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API_URL}/api/admin/contracts/${contract.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/admin/contracts`, form, { withCredentials: true });
      }
      toast.success(t('contract.saved'));
      onSaved();
      onClose();
    } catch (err) {
      toast.error(t('contract.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#111] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white text-lg">
            {isEdit ? t('contract.editContract') : t('contract.newContract')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Lender Section */}
          <div className="border border-white/10 rounded-md p-4 space-y-3">
            <h3 className="text-sm font-semibold text-amber-400">{t('contract.lender')} (MUTUANTE)</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">{t('contract.name')}</Label>
                <Input value={form.lender_name} onChange={e => setField('lender_name', e.target.value)}
                  className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="lender-name" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">CPF</Label>
                <Input value={form.lender_cpf} onChange={e => setField('lender_cpf', e.target.value)}
                  placeholder="000.000.000-00" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="lender-cpf" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">{t('contract.nationality')}</Label>
                <Input value={form.lender_nationality} onChange={e => setField('lender_nationality', e.target.value)}
                  className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="lender-nationality" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">{t('contract.address')}</Label>
                <Input value={form.lender_address} onChange={e => setField('lender_address', e.target.value)}
                  className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="lender-address" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">{t('contract.whatsapp')}</Label>
                <Input type="tel" value={form.lender_phone} onChange={e => setField('lender_phone', e.target.value)}
                  placeholder="55 11 99999-9999" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="lender-phone" />
              </div>
            </div>
          </div>

          {/* Borrower Section */}
          <div className="border border-white/10 rounded-md p-4 space-y-3">
            <h3 className="text-sm font-semibold text-blue-400">{t('contract.borrower')} (MUTUÁRIO)</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">{t('contract.companyName')}</Label>
                <Input value={form.borrower_name} onChange={e => setField('borrower_name', e.target.value)}
                  className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="borrower-name" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">CNPJ</Label>
                <Input value={form.borrower_cnpj} onChange={e => setField('borrower_cnpj', e.target.value)}
                  placeholder="00.000.000/0000-00" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="borrower-cnpj" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">{t('contract.address')}</Label>
                <Input value={form.borrower_address} onChange={e => setField('borrower_address', e.target.value)}
                  className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="borrower-address" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">{t('contract.representative')}</Label>
                <Input value={form.borrower_representative} onChange={e => setField('borrower_representative', e.target.value)}
                  className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="borrower-representative" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">{t('contract.whatsapp')}</Label>
                <Input type="tel" value={form.borrower_phone} onChange={e => setField('borrower_phone', e.target.value)}
                  placeholder="55 11 99999-9999" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="borrower-phone" />
              </div>
            </div>
          </div>

          {/* Terms Section */}
          <div className="border border-white/10 rounded-md p-4 space-y-3">
            <h3 className="text-sm font-semibold text-emerald-400">{t('contract.terms')}</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">{t('contract.amount')} (R$)</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={e => setField('amount', parseFloat(e.target.value) || 0)}
                  className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="contract-amount" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">{t('contract.interestMonthly')} (%)</Label>
                <Input type="number" step="0.1" value={form.interest_rate_monthly} onChange={e => setField('interest_rate_monthly', parseFloat(e.target.value) || 0)}
                  className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="contract-interest" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">{t('contract.termMonths')}</Label>
                <Input type="number" value={form.term_months} onChange={e => setField('term_months', parseInt(e.target.value) || 0)}
                  className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="contract-term" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">{t('contract.installments')}</Label>
                <Input type="number" value={form.installments} onChange={e => setField('installments', parseInt(e.target.value) || 0)}
                  className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="contract-installments" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">{t('contract.lateFee')} (%)</Label>
                <Input type="number" step="0.1" value={form.late_fee_pct} onChange={e => setField('late_fee_pct', parseFloat(e.target.value) || 0)}
                  className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="contract-late-fee" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">{t('contract.contractDate')}</Label>
                <Input type="date" value={form.contract_date} onChange={e => setField('contract_date', e.target.value)}
                  className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="contract-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">{t('contract.jurisdiction')}</Label>
                <Input value={form.jurisdiction} onChange={e => setField('jurisdiction', e.target.value)}
                  className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="contract-jurisdiction" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">{t('contract.purpose')}</Label>
                <Input value={form.purpose} onChange={e => setField('purpose', e.target.value)}
                  className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="contract-purpose" />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="text-zinc-300 border-white/20" data-testid="contract-form-cancel">
              {t('contract.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-white text-black hover:bg-zinc-200" data-testid="contract-form-save">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {t('contract.save')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Contract Detail View ─────────────────────────────────────
function ContractDetail({ contractId, onBack }) {
  const { t } = useTranslation();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signingRole, setSigningRole] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [sendingRole, setSendingRole] = useState(null);

  const fetchContract = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/contracts/${contractId}`, { withCredentials: true });
      setContract(res.data);
    } catch (err) {
      toast.error('Failed to load contract');
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => { fetchContract(); }, [fetchContract]);

  const handleSign = async (signatureData) => {
    try {
      await axios.post(`${API_URL}/api/admin/contracts/${contractId}/sign`, {
        role: signingRole,
        signature_data: signatureData,
      }, { withCredentials: true });
      toast.success(t('contract.signed'));
      setSigningRole(null);
      fetchContract();
    } catch (err) {
      toast.error(t('contract.signFailed'));
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/contracts/${contractId}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${contract?.contract_number || 'contract'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('PDF download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleSendRequest = async (role) => {
    setSendingRole(role);
    try {
      const res = await axios.post(`${API_URL}/api/admin/contracts/${contractId}/send-whatsapp`,
        { role }, { withCredentials: true });
      toast.success(`${t('contract.whatsappSent')}: ${res.data.sent_to}`);
      fetchContract();
    } catch (err) {
      const msg = err?.response?.data?.detail || t('contract.whatsappFailed');
      toast.error(msg);
    } finally {
      setSendingRole(null);
    }
  };


  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/admin/contracts/${contractId}`, { withCredentials: true });
      toast.success(t('contract.deleted'));
      onBack();
    } catch (err) {
      toast.error(t('contract.deleteFailed'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!contract) return null;

  const statusColor = {
    draft: 'bg-zinc-600',
    pending_signatures: 'bg-amber-600',
    signed: 'bg-emerald-600',
  };

  const statusLabel = {
    draft: t('contract.statusDraft'),
    pending_signatures: t('contract.statusPending'),
    signed: t('contract.statusSigned'),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-zinc-400 hover:text-white" data-testid="contract-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h2 className="text-white text-lg font-semibold">{contract.contract_number}</h2>
            <p className="text-zinc-500 text-xs">{t('contract.contractDate')}: {contract.contract_date}</p>
          </div>
          <Badge className={`${statusColor[contract.status] || 'bg-zinc-600'} text-white text-xs ml-2`}>
            {contract.status === 'signed' && <Lock className="w-3 h-3 mr-1" />}
            {statusLabel[contract.status] || contract.status}
          </Badge>
        </div>
        <div className="flex gap-2">
          {contract.status !== 'signed' && (
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} className="text-zinc-300 border-white/20" data-testid="contract-edit-btn">
              <Edit className="w-3 h-3 mr-1" /> {t('contract.edit')}
            </Button>
          )}
          <Button size="sm" onClick={handleDownloadPdf} disabled={downloading} className="bg-white text-black hover:bg-zinc-200" data-testid="contract-pdf-btn">
            {downloading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
            PDF
          </Button>
          {contract.status !== 'signed' && (
            <Button size="sm" variant="ghost" onClick={() => setDeleteOpen(true)} className="text-red-400 hover:text-red-300" data-testid="contract-delete-btn">
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>

      {contract.status === 'signed' && (
        <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5" data-testid="contract-locked-banner">
          <Lock className="w-4 h-4 text-emerald-400 shrink-0" />
          <p className="text-xs text-emerald-200/90 leading-relaxed">
            {t('contract.lockedNotice')}
          </p>
        </div>
      )}

      {/* Contract Document */}
      <Card className="bg-[#111] border-white/10">
        <CardContent className="p-6 sm:p-8">
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Title */}
            <div className="text-center space-y-1 pb-4 border-b border-white/10">
              <h1 className="text-xl font-bold text-white tracking-wide">CONTRATO DE MÚTUO</h1>
              <p className="text-zinc-400 text-sm">金銭消費貸借契約書</p>
              <p className="text-zinc-600 text-xs mt-2">Nº {contract.contract_number}</p>
            </div>

            {/* Clauses */}
            {contract.clauses?.map(([heading, body], i) => (
              <div key={i} className="space-y-1.5">
                {heading && (
                  <h3 className="text-sm font-bold text-zinc-200 tracking-wide">{heading}</h3>
                )}
                <p className="text-sm text-zinc-400 leading-relaxed whitespace-pre-line pl-1">
                  {body}
                </p>
              </div>
            ))}

            {/* Signature Section */}
            <div className="border-t border-white/10 pt-6 mt-8 space-y-6">
              <h3 className="text-sm font-bold text-white">{t('contract.signatures')} (電子署名)</h3>

              {/* Lender Signature */}
              <div className="border border-white/10 rounded-md p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-amber-400">{t('contract.lender')} (MUTUANTE)</span>
                    <p className="text-xs text-zinc-500">{contract.lender_name}</p>
                  </div>
                  {contract.lender_signature ? (
                    <Badge className="bg-emerald-600/20 text-emerald-400 text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" /> {t('contract.signed')}
                    </Badge>
                  ) : (
                    <Badge className="bg-zinc-700 text-zinc-400 text-xs">
                      <Clock className="w-3 h-3 mr-1" /> {t('contract.unsigned')}
                    </Badge>
                  )}
                </div>
                {contract.lender_signature ? (
                  <div className="space-y-1">
                    <img src={contract.lender_signature} alt="Lender signature" className="h-16 rounded bg-white p-1" data-testid="lender-sig-img" />
                    <p className="text-xs text-zinc-600">{contract.lender_signed_at?.slice(0, 19)}</p>
                  </div>
                ) : signingRole === 'lender' ? (
                  <SignaturePad
                    signerLabel={`${t('contract.signAs')} ${contract.lender_name}`}
                    onSave={handleSign}
                    onCancel={() => setSigningRole(null)}
                  />
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setSigningRole('lender')}
                      className="text-amber-400 border-amber-500/30 hover:bg-amber-500/10" data-testid="sign-lender-btn">
                      <Pen className="w-3 h-3 mr-1" /> {t('contract.signNow')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleSendRequest('lender')}
                      disabled={sendingRole === 'lender'}
                      className="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10" data-testid="whatsapp-lender-btn">
                      {sendingRole === 'lender' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <MessageCircle className="w-3 h-3 mr-1" />}
                      {t('contract.whatsappRequest')}
                    </Button>
                    {contract.lender_request_sent_at && (
                      <span className="text-[10px] text-zinc-500">{t('contract.requestSent')}: {contract.lender_request_sent_at.slice(0, 10)}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Borrower Signature */}
              <div className="border border-white/10 rounded-md p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-blue-400">{t('contract.borrower')} (MUTUÁRIO)</span>
                    <p className="text-xs text-zinc-500">{contract.borrower_name}</p>
                  </div>
                  {contract.borrower_signature ? (
                    <Badge className="bg-emerald-600/20 text-emerald-400 text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" /> {t('contract.signed')}
                    </Badge>
                  ) : (
                    <Badge className="bg-zinc-700 text-zinc-400 text-xs">
                      <Clock className="w-3 h-3 mr-1" /> {t('contract.unsigned')}
                    </Badge>
                  )}
                </div>
                {contract.borrower_signature ? (
                  <div className="space-y-1">
                    <img src={contract.borrower_signature} alt="Borrower signature" className="h-16 rounded bg-white p-1" data-testid="borrower-sig-img" />
                    <p className="text-xs text-zinc-600">{contract.borrower_signed_at?.slice(0, 19)}</p>
                  </div>
                ) : signingRole === 'borrower' ? (
                  <SignaturePad
                    signerLabel={`${t('contract.signAs')} ${contract.borrower_name}`}
                    onSave={handleSign}
                    onCancel={() => setSigningRole(null)}
                  />
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setSigningRole('borrower')}
                      className="text-blue-400 border-blue-500/30 hover:bg-blue-500/10" data-testid="sign-borrower-btn">
                      <Pen className="w-3 h-3 mr-1" /> {t('contract.signNow')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleSendRequest('borrower')}
                      disabled={sendingRole === 'borrower'}
                      className="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10" data-testid="whatsapp-borrower-btn">
                      {sendingRole === 'borrower' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <MessageCircle className="w-3 h-3 mr-1" />}
                      {t('contract.whatsappRequest')}
                    </Button>
                    {contract.borrower_request_sent_at && (
                      <span className="text-[10px] text-zinc-500">{t('contract.requestSent')}: {contract.borrower_request_sent_at.slice(0, 10)}</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Legal Footer */}
            <p className="text-[10px] text-zinc-600 text-center pt-4 border-t border-white/5">
              Este documento constitui um instrumento particular de contrato eletrônico, com validade jurídica nos termos da Lei nº 14.063/2020.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <ContractFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={fetchContract}
        contract={contract}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="bg-[#111] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{t('contract.deleteConfirm')}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {t('contract.deleteWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-zinc-300 border-white/20">{t('contract.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
              {t('contract.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────
export default function LoanContractPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchContracts();
  }, [user, navigate]);

  const fetchContracts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/contracts`, { withCredentials: true });
      setContracts(res.data);
    } catch (err) {
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  if (selectedId) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ContractDetail
            contractId={selectedId}
            onBack={() => { setSelectedId(null); fetchContracts(); }}
          />
        </div>
      </div>
    );
  }

  const statusIcon = {
    draft: <Clock className="w-3.5 h-3.5 text-zinc-400" />,
    pending_signatures: <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />,
    signed: <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />,
  };

  const statusLabel = {
    draft: t('contract.statusDraft'),
    pending_signatures: t('contract.statusPending'),
    signed: t('contract.statusSigned'),
  };

  const fmtBRL = (v) => `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <a href="/admin" className="text-zinc-500 hover:text-white transition-colors" data-testid="contract-back-admin">
              <ArrowLeft className="w-5 h-5" />
            </a>
            <FileText className="w-7 h-7 text-white" />
            <div>
              <h1 className="text-2xl font-bold text-white">{t('contract.title')}</h1>
              <p className="text-zinc-500 text-sm">{t('contract.subtitle')}</p>
            </div>
          </div>
          <Button onClick={() => setFormOpen(true)} className="bg-white text-black hover:bg-zinc-200" data-testid="new-contract-btn">
            <Plus className="w-4 h-4 mr-1" /> {t('contract.newContract')}
          </Button>
        </div>

        {/* Contract List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
          </div>
        ) : contracts.length === 0 ? (
          <Card className="bg-[#111] border-white/10">
            <CardContent className="py-16 text-center">
              <FileText className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">{t('contract.noContracts')}</p>
              <Button onClick={() => setFormOpen(true)} className="mt-4 bg-white text-black hover:bg-zinc-200" data-testid="new-contract-empty-btn">
                <Plus className="w-4 h-4 mr-1" /> {t('contract.createFirst')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {contracts.map((c) => (
              <Card
                key={c.id}
                className="bg-[#111] border-white/10 hover:border-white/20 transition-colors cursor-pointer"
                onClick={() => setSelectedId(c.id)}
                data-testid={`contract-item-${c.id}`}
              >
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-md bg-white/5 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-zinc-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{c.contract_number}</p>
                      <p className="text-zinc-500 text-xs">
                        {c.lender_name} → {c.borrower_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-white text-sm font-mono">{fmtBRL(c.amount)}</span>
                    <div className="flex items-center gap-1.5">
                      {statusIcon[c.status]}
                      <span className="text-xs text-zinc-400">{statusLabel[c.status]}</span>
                    </div>
                    <div className="flex gap-1">
                      {c.lender_signed && <Badge className="bg-amber-600/20 text-amber-400 text-[10px] px-1.5">{t('contract.lenderShort')}</Badge>}
                      {c.borrower_signed && <Badge className="bg-blue-600/20 text-blue-400 text-[10px] px-1.5">{t('contract.borrowerShort')}</Badge>}
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-600" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        <ContractFormModal
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSaved={fetchContracts}
          contract={null}
        />
      </div>
    </div>
  );
}
